import {
  Mesh,
  SphereGeometry,
  TorusGeometry,
  PlaneGeometry,
  Vector3
} from 'three';
import { Ability } from './Ability.js';
import { settings } from '../config/settings.js';
import { ParticleShape } from '../particles/ParticleSystem.js';
import { RateEmitter } from '../particles/ParticleEngine.js';
import { BurstMode } from '../effects/BurstSphere.js';
import { DecalType } from '../effects/GroundDecals.js';
import { frame } from '../core/FrameUniforms.js';
import { LAYER } from '../core/Layers.js';
import { saturate, Easing } from '../utils/math.js';
import { getColor } from '../utils/color.js';
import {
  ConstructStyle,
  ConstructGlyph,
  createConstructMaterial,
  createConstructGlyphMaterial,
  syncConstructMaterial
} from '../materials/ConstructMaterial.js';
import { pointOnRing } from './ConstructUtils.js';

const _pos = new Vector3();
const _dir = new Vector3();
const _emit = {};

/**
 * EVENT HORIZON — a zone whose climax is an implosion.
 *
 * The readable threat is acceleration rather than expansion: rings precess
 * faster, the accretion disc flattens and particles visibly travel inward. A
 * short visual vacuum precedes the snap. This is a setup/pull tool in combat,
 * with the burst occurring only after opponents have been asked to react.
 */
export class EventHorizonAbility extends Ability {
  constructor(context) {
    super('horizon', context);
  }

  createShaders() {
    this.coreGeometry = new SphereGeometry(1, 36, 24);
    this.coreMaterial = createConstructMaterial(ConstructStyle.VOID, { normal: true });
    this.core = new Mesh(this.coreGeometry, this.coreMaterial);
    this.core.layers.set(LAYER.VFX);
    this.core.renderOrder = 11;
    this.group.add(this.core);

    this.ringGeometry = new TorusGeometry(1, 0.035, 8, 64);
    this.rings = [];
    for (let i = 0; i < 4; i++) {
      const material = createConstructMaterial(ConstructStyle.ENERGY);
      const mesh = new Mesh(this.ringGeometry, material);
      mesh.layers.set(LAYER.VFX);
      mesh.renderOrder = 12;
      this.group.add(mesh);
      this.rings.push({ mesh, material });
    }

    this.fieldGeometry = new PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.fieldMaterial = createConstructGlyphMaterial(ConstructGlyph.VOID);
    this.field = new Mesh(this.fieldGeometry, this.fieldMaterial);
    this.field.layers.set(LAYER.VFX);
    this.field.renderOrder = 8;
    this.group.add(this.field);

    this.centre = new Vector3();
    this._seed = 0;
    this._collapsed = false;
  }

  createParticles() {
    this.grains = this.ctx.particles.get('horizon.grains', {
      capacity: 4200,
      shape: ParticleShape.STREAK,
      additive: true,
      stretch: true,
      softFade: 0.45
    });
    this.grains.uniforms.uGravity.value.set(0, 0, 0);
    this.grains.uniforms.uDrag.value = 0.25;
    this.grains.uniforms.uEndSize.value = 0.08;
    this.grains.uniforms.uFadeOut.value = 0.45;
    this.grainEmitter = new RateEmitter();
  }

  get impactDuration() {
    const c = settings.horizon;
    return Math.max(0.2, (c.gatherTime + c.aftermath) * settings.global.lifetime);
  }

  get fadeDuration() {
    return Math.max(0.1, settings.horizon.fadeTime);
  }

  get instanceCount() {
    return 6;
  }

  lightShimmer() {
    const c = settings.horizon;
    return 0.7 + 0.3 * Math.sin(this.age * c.pulseSpeed) ** 2;
  }

  onSpawn() {
    this._seed = Math.random() * 10;
    this._collapsed = false;
    this.grainEmitter.reset();
    this.core.visible = true;
    this.field.visible = false;
    for (const ring of this.rings) ring.mesh.visible = false;
    this._syncSeed();
  }

  _syncSeed() {
    const c = settings.horizon;
    this.core.position.copy(this.position).setY(c.travelHeight);
    this.core.scale.setScalar(c.seedSize * (0.8 + 0.2 * Math.sin(this.age * 16)));
    syncConstructMaterial(this.coreMaterial, c, {
      seed: this._seed,
      reveal: 1,
      fade: 1,
      pulse: 0.5
    });
  }

  onTravel(dt) {
    this._syncSeed();
    const c = settings.horizon;
    const count = this.grainEmitter.tick(dt, c.seedTrailRate);
    if (count > 0) this._emitGrains(this.core.position, c.seedSize * 2, count, c.seedTrailSpeed);
  }

  onImpact() {
    const c = settings.horizon;
    this.pointAt(1, this.centre).setY(0);
    this.position.copy(this.centre).setY(c.height);
    this.field.visible = true;
    for (const ring of this.rings) ring.mesh.visible = true;
    this.lightBoost = c.arrivalLight;

    this.ctx.decals.spawn(DecalType.DUSTRING, this.centre, {
      radius: c.zoneRadius,
      life: c.gatherTime + c.aftermath,
      intensity: c.fieldIntensity,
      colorA: getColor(c.colorA),
      colorB: getColor(c.colorB)
    });
  }

  _emitGrains(centre, radius, count, speed) {
    const c = settings.horizon;
    const g = settings.global;
    const angle = Math.random() * Math.PI * 2;
    pointOnRing(_pos, centre, radius, angle, (Math.random() - 0.5) * c.heightSpread);
    _emit.position = _pos;
    _emit.radius = c.spawnThickness;
    _emit.direction = _dir.copy(centre).sub(_pos).normalize();
    _emit.speed = speed * g.particleSpeed;
    _emit.speedVariance = 0.35;
    _emit.spread = 0.12;
    _emit.inherit = null;
    _emit.anchor = centre;
    _emit.size = c.particleSize * g.particleSize;
    _emit.sizeVariance = 0.65;
    _emit.life = c.particleLife * g.particleLifetime;
    _emit.lifeVariance = 0.35;
    _emit.spin = 0;
    _emit.tint = null;
    _emit.time = frame.uTime.value;
    this.grains.emit(Math.round(count * g.particleCount), _emit);
  }

  _collapse() {
    if (this._collapsed) return;
    this._collapsed = true;
    const c = settings.horizon;
    const g = settings.global;
    const centre = _pos.copy(this.centre).setY(c.height);

    this.ctx.bursts.spawn(BurstMode.AIR, centre, {
      radius: c.coreRadius * 0.3,
      endRadius: c.zoneRadius * c.burstRadius,
      life: c.burstLife,
      intensity: c.burstIntensity,
      opacity: 0.8,
      colorA: getColor(c.colorC),
      colorB: getColor(c.colorB),
      colorC: getColor(c.colorA)
    });
    this.ctx.decals.spawn(DecalType.SHOCKWAVE, this.centre, {
      radius: c.zoneRadius * 1.3,
      life: c.burstLife,
      intensity: c.burstIntensity,
      width: 0.08,
      colorA: getColor(c.colorC),
      colorB: getColor(c.colorB)
    });
    this._emitGrains(centre, c.zoneRadius * 0.25, c.burstParticles, c.burstSpeed);
    this.lightBoost = c.impactLight;
    this.ctx.shake.add(c.impactShake * g.cameraShake, c.shakeDuration);
    this.ctx.flash.trigger(getColor(c.colorC), c.impactFlash);
  }

  onFade(dt, t) {
    const c = settings.horizon;
    const fade = t <= 1 ? 1 : 1 - (t - 1);
    const scaledGather = c.gatherTime * settings.global.lifetime;
    const gather = saturate(this.impactTime / Math.max(0.01, scaledGather));
    const compression = Easing.inCubic(gather);
    if (gather >= 1) this._collapse();

    const radius = c.zoneRadius * (1 - compression * c.compression);
    const coreScale = this._collapsed
      ? c.coreRadius * (0.45 + 0.12 * Math.sin(this.age * c.pulseSpeed))
      : c.coreRadius * Easing.outCubic(saturate(gather * 2));
    this.core.position.copy(this.centre).setY(c.height);
    this.core.scale.setScalar(coreScale * fade);
    this.core.rotation.y = this.age * c.orbitSpeed;
    syncConstructMaterial(this.coreMaterial, c, {
      seed: this._seed,
      reveal: 1,
      fade,
      pulse: compression
    });

    const spin = c.orbitSpeed * (1 + compression * c.acceleration);
    for (let i = 0; i < this.rings.length; i++) {
      const ring = this.rings[i];
      const r = radius * (0.52 + i * 0.15);
      ring.mesh.position.copy(this.centre).setY(c.height + (i - 1.5) * c.ringSpacing);
      ring.mesh.scale.setScalar(r);
      ring.mesh.rotation.set(
        Math.PI / 2 + Math.sin(this.age * 0.7 + i) * c.precession,
        this.age * spin * (i % 2 ? -1 : 1),
        Math.cos(this.age * 0.5 + i) * c.precession
      );
      syncConstructMaterial(ring.material, c, {
        seed: this._seed + i,
        reveal: Easing.outCubic(saturate(gather * 2 - i * 0.08)),
        fade,
        pulse: compression
      });
    }

    const fieldSize = c.zoneRadius * 2.35;
    this.field.position.copy(this.centre).setY(c.fieldHeight);
    this.field.scale.set(fieldSize, fieldSize, fieldSize);
    this.field.rotation.y = -this.age * spin * 0.15;
    syncConstructMaterial(this.fieldMaterial, c, {
      seed: this._seed,
      reveal: Easing.outCubic(saturate(gather * 3)),
      fade,
      pulse: compression
    });

    if (!this._collapsed) {
      const count = this.grainEmitter.tick(dt, c.gatherRate * (0.35 + gather));
      if (count > 0) this._emitGrains(this.position, c.zoneRadius, count, c.gatherSpeed * (1 + gather));
      this.ctx.shake.rumble(c.gatherRumble * compression * settings.global.cameraShake, dt);
    }
  }

  onDestroy() {
    this.core.visible = false;
    this.field.visible = false;
    for (const ring of this.rings) ring.mesh.visible = false;
  }

  dispose() {
    this.coreGeometry.dispose();
    this.ringGeometry.dispose();
    this.fieldGeometry.dispose();
    this.coreMaterial.dispose();
    this.fieldMaterial.dispose();
    for (const ring of this.rings) ring.material.dispose();
    super.dispose();
  }
}
